import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/Jimmy.css';
import FileInput from '../components/FileInput/FileInput';
import axios from 'axios';
import logoj from '../assets/logo j.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUp,
    faBolt,
    faChevronLeft,
    faChevronRight,
    faCircleCheck,
    faFileLines,
    faGear,
    faPaperclip,
    faPlus,
    faQuestionCircle,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import FirstChat from '../components/FirstChat/FirstChat';
import ConfigChat from '../components/ConfigChat/ConfigChat';
import { auth, db } from '../firebase/firebaseConfig';
import { doc, onSnapshot } from 'firebase/firestore';
import bgShapes from '../assets/BG-shapes-1.png';

const backendBaseUrl = (process.env.REACT_APP_URL_BACK || 'http://localhost:8000').replace(/\/$/, '');

function Jimmy() {
    const [mensagem, setMessage] = useState('');
    const [mensagens, setMessages] = useState([]);
    const [isSidebarVisible, setIsSidebarVisible] = useState(() => window.innerWidth > 860);
    const [isFirstChatVisible, setIsFirstChatVisible] = useState(true);
    const [savedChats, setSavedChats] = useState([]);
    const [currentFileName, setCurrentFileName] = useState(null);
    const [currentDocumentId, setCurrentDocumentId] = useState(null);
    const [currentChat, setCurrentChat] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isConfigVisible, setIsConfigVisible] = useState(false);
    const [isChatSaved, setIsChatSaved] = useState(false);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [requestError, setRequestError] = useState('');
    const chatEndRef = useRef(null);
    const fileInputRef = useRef();
    const userEmail = auth.currentUser?.email || 'anonymous';

    useEffect(() => {
        let unsubscribe;

        if (auth.currentUser) {
            const docRef = doc(db, `usuarios/${auth.currentUser.email}`);
            unsubscribe = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    setProfilePhoto(docSnap.data().photoURL || null);
                }
            }, (error) => {
                console.error('Erro ao acompanhar a foto de perfil:', error);
            });
        }

        return () => unsubscribe?.();
    }, []);

    const fetchSavedChats = useCallback(async () => {
        try {
            const response = await axios.get(`${backendBaseUrl}/list-chats`, {
                params: { user_email: userEmail },
            });
            setSavedChats(response.data.chats || []);
        } catch (error) {
            console.error('Erro ao buscar as análises salvas:', error);
        }
    }, [userEmail]);

    useEffect(() => {
        fetchSavedChats();
    }, [fetchSavedChats]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    const saveChat = async (messagesToSave) => {
        if (!messagesToSave.length) return;

        let fileName = currentFileName;
        if (!isChatSaved && !fileName) {
            fileName = `Análise_${String(savedChats.length + 1).padStart(2, '0')}.txt`;
            setCurrentFileName(fileName);
        }

        const chatData = messagesToSave.map((msg) => `${msg.type}: ${msg.text}`).join('\n');

        try {
            await axios.post(`${backendBaseUrl}/save-chat`, {
                mensagem: chatData,
                file_name: fileName,
                user_email: userEmail,
                document_id: currentDocumentId || null,
            });
            setIsChatSaved(true);
            await fetchSavedChats();
        } catch (error) {
            console.error('Erro ao salvar a análise:', error);
        }
    };

    const handleUpload = (uploadResult) => {
        if (!uploadResult?.document_id) {
            setRequestError(uploadResult?.feedback || 'Não foi possível indexar o documento.');
            return;
        }

        const fileName = uploadResult.file_name || 'Documento PDF';
        setCurrentDocumentId(uploadResult.document_id);
        setCurrentFileName(fileName);
        setIsChatSaved(false);
        setCurrentChat(null);
        setRequestError('');
        setMessages([{
            type: 'ia',
            text: `Documento "${fileName}" indexado. Posso resumir, localizar cláusulas, apontar riscos ou responder perguntas específicas com base no conteúdo.`,
        }]);
        setIsFirstChatVisible(false);
    };

    const sendMessage = async (messageOverride) => {
        const text = typeof messageOverride === 'string' ? messageOverride : mensagem;
        if (!text.trim() || isGenerating || !currentDocumentId) return;

        const userMessage = { text: text.trim(), type: 'user' };
        const pendingMessage = { text: 'Analisando o documento...', type: 'ia', pending: true };
        setMessages((previous) => [...previous, userMessage, pendingMessage]);
        setMessage('');
        setRequestError('');
        setIsGenerating(true);

        try {
            const response = await axios.post(`${backendBaseUrl}/sendMessage`, {
                mensagem: userMessage.text,
                user_email: userEmail,
                file_name: currentFileName || null,
                document_id: currentDocumentId,
            });

            const aiMessage = {
                text: response.data.resposta_ia || 'O modelo não retornou conteúdo.',
                type: 'ia',
            };
            const completedMessages = [...mensagens, userMessage, aiMessage];
            setMessages(completedMessages);
            await saveChat(completedMessages);
        } catch (error) {
            const detail = error.response?.data?.detail || error.response?.data?.error || error.message;
            setMessages((previous) => previous.slice(0, -1));
            setRequestError(`Não foi possível consultar o Jimmy-A1. ${detail || 'Verifique o backend e tente novamente.'}`);
        } finally {
            setIsGenerating(false);
        }
    };

    const loadChat = async (folderName) => {
        setIsLoadingChat(true);
        setRequestError('');

        try {
            const response = await axios.get(`${backendBaseUrl}/load-chat/${encodeURIComponent(userEmail)}/${encodeURIComponent(folderName)}`);
            const content = response.data.content;

            if (!content) {
                throw new Error(response.data.error || 'Conteúdo da análise não encontrado.');
            }

            const loadedMessages = [];
            let currentMessage = '';

            content.split('\n').forEach((line) => {
                if (line.startsWith('user: ') || line.startsWith('ia: ')) {
                    if (currentMessage) {
                        const [type, ...textParts] = currentMessage.split(': ');
                        loadedMessages.push({ type, text: textParts.join(': ') });
                    }
                    currentMessage = line;
                } else {
                    currentMessage += `\n${line}`;
                }
            });

            if (currentMessage) {
                const [type, ...textParts] = currentMessage.split(': ');
                loadedMessages.push({ type, text: textParts.join(': ') });
            }

            setMessages(loadedMessages);
            setIsFirstChatVisible(false);
            setCurrentFileName(response.data.file_name || folderName);
            setCurrentDocumentId(response.data.document_id || null);
            setCurrentChat(folderName);
            setIsChatSaved(true);

            if (response.data.error) {
                setRequestError(response.data.error);
            }
        } catch (error) {
            setRequestError(error.message || 'Erro ao carregar a análise.');
        } finally {
            setIsLoadingChat(false);
        }
    };

    const newChat = () => {
        setIsFirstChatVisible(true);
        setMessages([]);
        setCurrentFileName(null);
        setCurrentDocumentId(null);
        setCurrentChat(null);
        setIsChatSaved(false);
        setRequestError('');
    };

    const deleteChat = async (folderName) => {
        if (!window.confirm(`Excluir a análise "${folderName}"?`)) return;

        try {
            await axios.post(`${backendBaseUrl}/delete-chat/${encodeURIComponent(userEmail)}/${encodeURIComponent(folderName)}`);
            if (currentChat === folderName) newChat();
            await fetchSavedChats();
        } catch (error) {
            setRequestError('Não foi possível excluir a análise.');
        }
    };

    const handleComposerKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={`jimmy-workspace ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
            <img className="bgshapes" src={bgShapes} alt="" aria-hidden="true" />

            <aside className="jimmy-history" aria-label="Análises salvas">
                <div className="jimmy-history-header">
                    {isSidebarVisible && (
                        <div>
                            <span>Biblioteca</span>
                            <strong>Análises recentes</strong>
                        </div>
                    )}
                    <button
                        className="jimmy-icon-button"
                        onClick={() => setIsSidebarVisible((visible) => !visible)}
                        type="button"
                        title={isSidebarVisible ? 'Recolher histórico' : 'Abrir histórico'}
                    >
                        <FontAwesomeIcon icon={isSidebarVisible ? faChevronLeft : faChevronRight} />
                    </button>
                </div>

                <button className="jimmy-new-analysis" onClick={newChat} type="button" disabled={isGenerating}>
                    <FontAwesomeIcon icon={faPlus} />
                    {isSidebarVisible && <span>Nova análise</span>}
                </button>

                <div className="jimmy-history-list">
                    {isSidebarVisible && savedChats.length === 0 && (
                        <p className="jimmy-history-empty">As análises salvas aparecerão aqui.</p>
                    )}
                    {isSidebarVisible && savedChats.map((chat) => (
                        <div className={`jimmy-history-item ${currentChat === chat ? 'active' : ''}`} key={chat}>
                            <button onClick={() => loadChat(chat)} type="button" disabled={isGenerating || isLoadingChat}>
                                <FontAwesomeIcon icon={faFileLines} />
                                <span>{chat.replace('.txt', '')}</span>
                            </button>
                            <button
                                className="jimmy-delete-analysis"
                                onClick={() => deleteChat(chat)}
                                type="button"
                                title="Excluir análise"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    ))}
                </div>

                <div className="jimmy-history-footer">
                    <button className="jimmy-icon-button" onClick={() => setIsConfigVisible(true)} type="button" title="Configurações">
                        <FontAwesomeIcon icon={faGear} />
                    </button>
                    <button className="jimmy-icon-button" type="button" title="Ajuda">
                        <FontAwesomeIcon icon={faQuestionCircle} />
                    </button>
                </div>
            </aside>

            <main className="jimmy-chat-main">
                {isConfigVisible && <ConfigChat onClose={() => setIsConfigVisible(false)} />}

                <header className="jimmy-chat-header">
                    <button
                        className="jimmy-mobile-history"
                        onClick={() => setIsSidebarVisible(true)}
                        type="button"
                        aria-label="Abrir histórico"
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                    <div className="jimmy-document-heading">
                        <span className="jimmy-document-icon"><FontAwesomeIcon icon={faFileLines} /></span>
                        <div>
                            <small>Leitor Jimmy-A1</small>
                            <strong>{currentFileName || 'Nenhum documento aberto'}</strong>
                        </div>
                    </div>

                    <div className="jimmy-model-status">
                        <span className="jimmy-status-dot" />
                        <div>
                            <small>Modelo</small>
                            <strong><FontAwesomeIcon icon={faBolt} /> Jimmy-A1</strong>
                        </div>
                    </div>
                </header>

                {requestError && (
                    <div className="jimmy-request-error" role="alert">
                        <span>{requestError}</span>
                        <button onClick={() => setRequestError('')} type="button" aria-label="Fechar aviso">×</button>
                    </div>
                )}

                {isLoadingChat ? (
                    <div className="jimmy-loading" aria-live="polite">
                        <span className="jimmy-loading-spinner" />
                        <strong>Reabrindo análise</strong>
                        <p>O PDF está sendo indexado novamente.</p>
                    </div>
                ) : isFirstChatVisible ? (
                    <FirstChat
                        onFileSelect={() => setRequestError('')}
                        onFileRemove={() => setRequestError('')}
                        onUpload={handleUpload}
                        userEmail={userEmail}
                    />
                ) : (
                    <>
                        <section className="jimmy-message-list" aria-live="polite" aria-busy={isGenerating}>
                            <div className="jimmy-message-inner">
                                {mensagens.map((msg, index) => (
                                    <article className={`jimmy-message ${msg.type === 'ia' ? 'assistant' : 'user'}`} key={`${msg.type}-${index}`}>
                                        <div className="jimmy-message-avatar">
                                            {msg.type === 'ia' ? (
                                                <img src={logoj} alt="" />
                                            ) : profilePhoto ? (
                                                <img src={profilePhoto} alt="" />
                                            ) : (
                                                <span>{userEmail.charAt(0).toUpperCase()}</span>
                                            )}
                                        </div>
                                        <div className={`jimmy-message-content ${msg.pending ? 'pending' : ''}`}>
                                            <span className="jimmy-message-author">{msg.type === 'ia' ? 'Jimmy' : 'Você'}</span>
                                            <p>{msg.text}</p>
                                            {msg.pending && <span className="jimmy-typing"><i /><i /><i /></span>}
                                        </div>
                                    </article>
                                ))}
                                <div ref={chatEndRef} />
                            </div>
                        </section>

                        <footer className="jimmy-composer-area">
                            <div className="jimmy-suggestions">
                                <button onClick={() => sendMessage('Resuma o documento e destaque os pontos críticos.')} disabled={isGenerating} type="button">
                                    Resumir e destacar riscos
                                </button>
                                <button onClick={() => sendMessage('Liste as obrigações, prazos e penalidades previstos no documento.')} disabled={isGenerating} type="button">
                                    Mapear obrigações e prazos
                                </button>
                            </div>

                            <div className="jimmy-composer">
                                <FileInput
                                    ref={fileInputRef}
                                    onFileSelect={() => setRequestError('')}
                                    onFileRemove={() => setRequestError('')}
                                    onUpload={handleUpload}
                                    additionalClass="custom-file-input"
                                    userEmail={userEmail}
                                />
                                <button
                                    onClick={() => fileInputRef.current?.onChooseFile()}
                                    className="jimmy-attach-button"
                                    disabled={isGenerating}
                                    type="button"
                                    title="Trocar documento"
                                >
                                    <FontAwesomeIcon icon={faPaperclip} />
                                </button>
                                <textarea
                                    value={mensagem}
                                    onChange={(event) => setMessage(event.target.value)}
                                    onKeyDown={handleComposerKeyDown}
                                    placeholder="Pergunte sobre cláusulas, riscos, prazos ou fundamentos..."
                                    disabled={isGenerating}
                                    rows="1"
                                />
                                <button
                                    className="jimmy-send-button"
                                    onClick={() => sendMessage()}
                                    disabled={isGenerating || !mensagem.trim()}
                                    type="button"
                                    aria-label="Enviar mensagem"
                                >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                            </div>

                            <p className="jimmy-composer-note">
                                <FontAwesomeIcon icon={faCircleCheck} />
                                Respostas fundamentadas no documento. Revise antes do uso profissional.
                            </p>
                        </footer>
                    </>
                )}
            </main>
        </div>
    );
}

export default Jimmy;
